import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, FileText } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { FrameworkCard } from '@/components/FrameworkCard';
import { EssayCard } from '@/components/EssayCard';
import { ConcentricRings } from '@/components/Motifs';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';
import api from '@/lib/api';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] } })
};

export default function FoundationsPage() {
  usePageTitle('Foundations');
  const [frameworks, setFrameworks] = useState([]);
  const [essays, setEssays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/frameworks'),
      api.get('/essays?limit=3')
    ]).then(([fwRes, essayRes]) => {
      setFrameworks(fwRes.data);
      setEssays(essayRes.data.essays || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero */}
      <section className="hero-gradient relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1658237775407-aed6450f5851?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
            <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--silver))] mb-6">
              The Intellectual Body of Work
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.08] text-[hsl(var(--foreground))] mb-6">
              Foundations
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">
              The structured frameworks, essays, and archetype systems that form the intellectual architecture of The Cosmic Synthesist.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Three paths */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {[
              { icon: BookOpen, label: 'Framework Library', title: 'The structured work', desc: 'Named intellectual frameworks for understanding consciousness, identity, and power.', href: '/work', cta: 'Explore Frameworks', count: `${frameworks.length} frameworks` },
              { icon: FileText, label: 'Essays', title: 'The living body of work', desc: 'Long-form explorations of the patterns that shape how we think, lead, and build.', href: '/essays', cta: 'Read Essays', count: `${essays.length} recent` },
              { label: 'Sacred Feminine Archetypes', title: 'The archetype system', desc: 'Five thinking archetypes along the spectrum from Earthly to Cosmic. Discover yours.', href: '/sacred-feminine-archetypes', cta: 'Explore Archetypes', count: '5 archetypes' },
            ].map((path, i) => (
              <motion.div key={path.label} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                <Link to={path.href} className="group block h-full">
                  <div className="h-full rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 transition-all duration-300 hover:border-[rgba(201,168,76,0.35)] hover:bg-[rgba(27,77,68,0.12)]">
                    <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">{path.label}</p>
                    <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-semibold italic text-[hsl(var(--foreground))] mb-3 leading-[1.2]">{path.title}</h3>
                    <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))] mb-6">{path.desc}</p>
                    <div className="flex items-center justify-between">
                      <span className="font-['Inter'] text-xs text-[hsl(var(--silver))]">{path.count}</span>
                      <span className="inline-flex items-center gap-1.5 font-['Inter'] text-xs font-medium text-[hsl(var(--gold))] group-hover:gap-2.5 transition-all duration-300">
                        {path.cta} <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured frameworks */}
      {frameworks.length > 0 && (
        <section className="py-16 sm:py-24 section-mist">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">Featured</p>
                <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))]">From the Framework Library</h2>
              </div>
              <Link to="/work" className="hidden sm:inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {frameworks.slice(0, 3).map((fw, i) => (
                <motion.div key={fw._id || fw.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                  <FrameworkCard framework={fw} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent essays */}
      {essays.length > 0 && (
        <section className="py-16 sm:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-12">
              <div>
                <p className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4">Recent</p>
                <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))]">From the Essays</h2>
              </div>
              <Link to="/essays" className="hidden sm:inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                View all <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="space-y-0">
              {essays.map((essay, i) => (
                <motion.div key={essay._id || essay.slug} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
                  <EssayCard essay={essay} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quiz CTA */}
      <section className="py-16 sm:py-24 section-mist">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <ConcentricRings size={50} opacity={0.12} className="mx-auto mb-6" />
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-4">Which archetype leads your thinking?</h2>
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8">Take the 5-minute Sacred Feminine Archetype Quiz.</p>
          <Link to="/sacred-feminine-quiz">
            <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-10 py-6 rounded-lg hover:brightness-[1.03]">Take the Quiz</Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
